import { Button, Divider, Form, Input, Typography } from 'antd';
import { useChangePassword } from '@presentation/hooks/useProfile';
import { useAppMessage } from '@presentation/hooks/useAppMessage';
import { ValidationError } from '@core/errors/AppError';
import { setAntdFormErrors, snakeToCamelErrorKeys } from '@shared/utils/formErrors';
import { TwoFactorSetup } from '@presentation/components/settings/TwoFactorSetup';

interface ChangePasswordFormValues {
  currentPassword: string;
  password: string;
  passwordConfirmation: string;
}

export const SecurityTab = () => {
  const changePassword = useChangePassword();
  const [form] = Form.useForm<ChangePasswordFormValues>();
  const message = useAppMessage();

  const handleFinish = (values: ChangePasswordFormValues) => {
    changePassword.mutate(values, {
      onSuccess: () => {
        form.resetFields();
        message.success('Password updated.');
      },
      onError: (error) => {
        if (error instanceof ValidationError) {
          setAntdFormErrors(form, snakeToCamelErrorKeys(error.errors));
        }
      },
    });
  };

  return (
    <div className="space-y-10">
      <section>
        <Typography.Title level={5}>Change password</Typography.Title>
        <Form form={form} layout="vertical" className="max-w-md" onFinish={handleFinish}>
          <Form.Item
            name="currentPassword"
            label="Current password"
            rules={[{ required: true, message: 'Current password is required' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="password"
            label="New password"
            rules={[{ required: true, message: 'New password is required' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="passwordConfirmation"
            label="Confirm new password"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={changePassword.isPending}>
            Update password
          </Button>
        </Form>
      </section>

      <Divider />

      <section>
        <Typography.Title level={5}>Two-factor authentication</Typography.Title>
        <TwoFactorSetup />
      </section>
    </div>
  );
};
